import { type FC, useCallback, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { SelectChip, SelectMenuOption, SelectMenuSelectionMode } from "@stubhub/broadway-web";
import { useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { PriceBuckets, PriceBucketsEnum } from "src/shared/enums/enums";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";

const unselectedClasses = {
  label: css({ color: "text.subtle" }),
  icon: css({ color: "text.subtle" }),
};

const PRICE_LABELS: Record<PriceBuckets, string> = {
  [PriceBuckets.ONE]: "$",
  [PriceBuckets.TWO]: "$$",
  [PriceBuckets.THREE]: "$$$",
  [PriceBuckets.FOUR]: "$$$$",
};

const buildPriceLabel = (buckets: PriceBuckets[]): string | null => {
  if (buckets.length === 0) return null;
  const sorted = buckets.toSorted((a, b) => a - b);
  let contiguous = sorted.length > 1;
  for (let i = 1; i < sorted.length; i++) {
    if ((sorted[i] ?? 0) !== (sorted[i - 1] ?? 0) + 1) {
      contiguous = false;
      break;
    }
  }
  if (contiguous) {
    const first = sorted[0];
    const last = sorted.at(-1);
    if (first !== undefined && last !== undefined) return `${PRICE_LABELS[first]} - ${PRICE_LABELS[last]}`;
  }
  return sorted.map((b) => PRICE_LABELS[b]).join(", ");
};

export const BroadwayMedianPriceFilterChip: FC = () => {
  const storePageEvent = useStorePageEvent();
  const labelDefault = useContent("cid$#3829969#");
  const [state, dispatch, { setMedianPriceBucketFilter }] = useEventsGridContext();

  const activeBuckets = useMemo(
    () =>
      (state.filters.medianPriceBucketFilter ?? [])
        .map((b) => PriceBucketsEnum.asValueOrDefault(Number(b)))
        .filter((b): b is PriceBuckets => b !== undefined),
    [state.filters.medianPriceBucketFilter],
  );

  const label = buildPriceLabel(activeBuckets) ?? labelDefault;
  const value = useMemo(() => activeBuckets.map(String), [activeBuckets]);

  const handleChange = useCallback(
    (values: string[]) => {
      const newState = values
        .map(Number)
        .map((val) => PriceBucketsEnum.asValueOrDefault(val))
        .filter((val): val is PriceBuckets => val !== undefined);
      setMedianPriceBucketFilter(dispatch, state, newState);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.PRICE_FILTER_OPTION_CLICK, values.join(","));
    },
    [setMedianPriceBucketFilter, dispatch, state, storePageEvent],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.PRICE_FILTER_CHIP_TOGGLE, isOpen ? "open" : "close");
    },
    [storePageEvent],
  );

  return (
    <SelectChip
      label={label}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      selectionMode={SelectMenuSelectionMode.MultiSelect}
      value={value}
      {...(activeBuckets.length > 0 ? {} : { classes: unselectedClasses })}
    >
      <SelectMenuOption value={String(PriceBuckets.ONE)} label="$" />
      <SelectMenuOption value={String(PriceBuckets.TWO)} label="$$" />
      <SelectMenuOption value={String(PriceBuckets.THREE)} label="$$$" />
      <SelectMenuOption value={String(PriceBuckets.FOUR)} label="$$$$" />
    </SelectChip>
  );
};
